// web/src/routes/RecordDetail.tsx — record detail at /records/:id (wireframe 02,
// M4.2 / M4.8 / R15). Bound to api.getRecord: a quorum + possession-proof summary,
// the member roster with safety-number fingerprints + possession status, the
// ceremony history, and the audit slice. Actions: "Convene unseal" (break-glass)
// and "Transfer ownership" (metadata-only consequence dialog). METADATA ONLY — no
// shard ciphertext, key, or plaintext is ever read here (R11).
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Info, KeyRound, Lock, ShieldCheck, Zap } from 'lucide-react';
import type { AuditEntry, Principal, RecordDetail as RecordDetailData } from '../../../shared/types';
import { api } from '../api';
import { useAsync } from '../hooks';
import { useConsoleData } from './Console';
import { Icon } from '../components/ui/Icon';
import { Badge, EmptyState, FeedbackMessage } from '../components/ui';
import { BreakGlassDialog } from '../components/records/BreakGlassDialog';
import { useConveneUnseal } from '../components/records/useConveneUnseal';
import { ShareCeremonyPanel } from '../components/ceremony/ShareCeremonyPanel';
import { ProofIntervalControl } from '../components/records/ProofIntervalControl';
import {
  TransferOwnershipDialog,
  candidatesFrom,
} from '../components/records/TransferOwnershipDialog';
import type { CeremonyHolder } from '../components/ceremony/UnsealCeremony';
import {
  ago,
  convenenBlockReason,
  expiringBadge,
  freshProofs,
  isLiveCeremony,
  possessionBadge,
  quorumHealth,
  recordName,
  statusBadge,
  unsealLabel,
  windowLabel,
} from '../lib/records';

export function RecordDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { principal } = useConsoleData();
  return <RecordDetailInner id={id} principal={principal} />;
}

function RecordDetailInner({
  id,
  principal,
}: {
  id: string;
  principal: Principal;
}): React.JSX.Element {
  const navigate = useNavigate();
  const state = useAsync<RecordDetailData>(() => api.getRecord(id));
  const convene = useConveneUnseal(principal);
  // Owner-only audit slice (R11): api.recordAudit is gated server-side to the
  // record's owner (403 otherwise). Best-effort — a non-owner's 403 just leaves
  // the slice empty; the global feed is gone.
  const auditState = useAsync<AuditEntry[]>(
    React.useCallback(() => api.recordAudit(id), [id]),
  );

  const [showBreak, setShowBreak] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  if (state.loading && !state.data) {
    return (
      <div className="bgPage">
        <div className="bgDetailSkeleton" aria-busy="true" aria-label="Loading the record">
          <div className="bgSkeletonRow" />
          <div className="bgSkeletonRow" />
          <div className="bgSkeletonRow" />
        </div>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="bgPage">
        <Link to="/" className="bgBackLink">
          <Icon icon={ArrowLeft} size="xs" /> Back to records
        </Link>
        <div className="bgEmptyWrap">
          <EmptyState
            title="Record unavailable"
            subtitle={state.error ?? 'This record could not be loaded.'}
          />
        </div>
      </div>
    );
  }

  const { record, members, ceremonies } = state.data;
  // Prefer the owner-only audit endpoint; fall back to the detail's history slice.
  const auditEntries = auditState.data ?? state.data.history;
  const liveCount = ceremonies.filter(isLiveCeremony).length;
  const liveMembers = members.filter((m) => !m.shardRevoked);
  const summaryForHealth = {
    status: record.status,
    thresholdK: record.thresholdK,
    totalN: record.totalN,
    memberCount: liveMembers.length,
    provenCount: freshProofs(members),
  };
  const health = quorumHealth(summaryForHealth, liveCount > 0);
  const expiring = expiringBadge(ceremonies);
  const status = statusBadge(record.status);
  const name = recordName(record);
  const isOwner = record.ownerId === principal.id;
  const isMember =
    isOwner || members.some((m) => m.email === principal.email && !m.shardRevoked);
  const blockReason = convenenBlockReason({
    status: record.status,
    thresholdK: record.thresholdK,
    totalN: record.totalN,
    memberCount: liveMembers.length,
    isMember,
  });

  const holders: CeremonyHolder[] = liveMembers.map((m) => ({
    memberId: m.memberId,
    label: m.email,
    fingerprint: m.fingerprint ?? undefined,
  }));

  const candidates = candidatesFrom(members, record.ownerId);

  async function doTransfer(toOwnerId: string): Promise<void> {
    setTransferBusy(true);
    setTransferError(null);
    try {
      await api.transferOwnership(record.id, toOwnerId);
      setShowTransfer(false);
      setTransferBusy(false);
      await state.refetch();
    } catch (e) {
      setTransferError(e instanceof Error ? e.message : String(e));
      setTransferBusy(false);
    }
  }

  return (
    <div className="bgPage">
      <Link to="/" className="bgBackLink">
        <Icon icon={ArrowLeft} size="xs" /> Back to records
      </Link>

      <div className="bgDetailHead">
        <div className="bgDetailTitle">
          <span className="bgLock bgLockLg" aria-hidden="true">
            <Icon icon={Lock} size="md" />
          </span>
          <div>
            <h1 className="bgDetailName">{name}</h1>
            <div className="bgBadgeRow">
              <Badge tone={status.tone} label={status.label} />
              <span title={health.tooltip}>
                <Badge tone={health.tone} label={health.label} />
              </span>
              {expiring && (
                <span title={expiring.tooltip}>
                  <Badge tone={expiring.tone} label={expiring.label} />
                </span>
              )}
              <span className="bgSubtle">
                Owner: {isOwner ? 'you' : record.ownerId} · sensitivity {record.sensitivity}
              </span>
            </div>
          </div>
        </div>
        <div className="bgDetailActions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowBreak(true)}
            disabled={blockReason !== null}
            aria-describedby={blockReason ? 'bg-convene-blocked' : undefined}
          >
            <Icon icon={Zap} size="sm" /> Convene unseal
          </button>
          <button type="button" className="btn" onClick={() => setShowTransfer(true)}>
            <Icon icon={ArrowRightLeft} size="sm" /> Transfer ownership
          </button>
        </div>
      </div>

      {blockReason && (
        <div className="bgBlockedNote" id="bg-convene-blocked" role="note">
          <span className="bgBlockedIcon" aria-hidden="true">
            <Icon icon={Info} size="sm" />
          </span>
          <span>
            <strong style={{ color: 'var(--color-text)' }}>You can’t break the glass yet.</strong>{' '}
            {blockReason}
          </span>
        </div>
      )}

      <div className="bgTrustBar">
        <Icon icon={ShieldCheck} size="sm" tone="accent" />
        Shards are encrypted to each member. The server stores them but can't open them.
      </div>

      <div className="bgDetailGrid">
        <section className="bgSection">
          <header className="bgSectionHead">
            <div>
              <div className="kicker">Quorum</div>
              <h2 className="bgSectionTitle">{record.thresholdK} of {record.totalN}</h2>
            </div>
          </header>
          <div className="bgSectionBody bgKvList">
            <div className="bgKv">
              <span className="bgKvKey">Threshold</span>
              <span className="bgKvVal">{record.thresholdK} of {record.totalN} members</span>
            </div>
            <div className="bgKv">
              <span className="bgKvKey">Window</span>
              <span className="bgKvVal">{windowLabel(record.timeoutSeconds)}</span>
            </div>
            <div className="bgKv">
              <span className="bgKvKey">Created</span>
              <span className="bgKvVal">{ago(record.createdAt)}</span>
            </div>
          </div>
        </section>

        <section className="bgSection">
          <header className="bgSectionHead">
            <div>
              <div className="kicker">Possession proof</div>
              <h2 className="bgSectionTitle">
                {freshProofs(members)} of {liveMembers.length} fresh
              </h2>
            </div>
          </header>
          <div className="bgSectionBody bgKvList">
            {liveMembers.length === 0 ? (
              <p className="bgSubtle">No members hold a shard yet.</p>
            ) : (
              liveMembers.map((m) => {
                const pb = possessionBadge(m.possessionStatus);
                return (
                  <div className="bgKv" key={`pp-${m.memberId}`}>
                    <span className="bgKvKey bgKvKeyPlain">{m.email}</span>
                    <span className="bgKvVal">
                      <Badge tone={pb.tone} label={pb.label} />{' '}
                      <span className="bgSubtle">{ago(m.lastProofAt)}</span>
                    </span>
                  </div>
                );
              })
            )}
            {isOwner && (
              <ProofIntervalControl
                recordId={record.id}
                proofIntervalSec={record.proofIntervalSec}
                onChanged={() => void state.refetch()}
              />
            )}
          </div>
        </section>
      </div>

      <section className="bgSection">
        <header className="bgSectionHead">
          <div>
            <div className="kicker">Members &amp; fingerprints</div>
            <h2 className="bgSectionTitle">Roster</h2>
          </div>
        </header>
        <ul className="bgRoster">
          {members.map((m) => {
            const pb = possessionBadge(m.possessionStatus);
            return (
              <li key={m.memberId} className={`bgRosterRow ${m.shardRevoked ? 'bgRosterRevoked' : ''}`}>
                <span className="bgRosterKey" aria-hidden="true">
                  <Icon icon={KeyRound} size="sm" />
                </span>
                <span className="bgRosterWho">
                  <span className="bgRosterEmail">{m.email}</span>
                  <span className="bgFingerprint">
                    {m.fingerprint ? `fp: ${m.fingerprint}` : 'no key on file'}
                  </span>
                </span>
                <span className="bgRosterMeta">share #{m.shareIndex}</span>
                {m.shardRevoked ? (
                  <Badge tone="danger" label="Revoked" />
                ) : (
                  <span title={`Last proof: ${ago(m.lastProofAt)}`}>
                    <Badge tone={pb.tone} label={pb.label} />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <div className="bgDetailGrid">
        <section className="bgSection">
          <header className="bgSectionHead">
            <div>
              <div className="kicker">Ceremony history</div>
              <h2 className="bgSectionTitle">Unseals</h2>
            </div>
          </header>
          <div className="bgSectionBody">
            {ceremonies.length === 0 ? (
              <p className="bgSubtle">No unseal ceremonies yet for this record.</p>
            ) : (
              <ul className="bgCeremonyList">
                {ceremonies.map((c) => (
                  <li key={c.id} className="bgCeremonyRow">
                    <Badge
                      tone={c.status === 'unlocked' ? 'success' : isLiveCeremony(c) ? 'info' : 'neutral'}
                      label={unsealLabel(c.status)}
                    />
                    <span className="bgSubtle">
                      {c.submittedCount} of {c.thresholdK} shards · {ago(c.createdAt)}
                    </span>
                    {isLiveCeremony(c) && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm bgCeremonyJump"
                        onClick={() => navigate(`/unseal/${c.id}`)}
                      >
                        Open
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="bgSection">
          <header className="bgSectionHead">
            <div>
              <div className="kicker">Audit slice</div>
              <h2 className="bgSectionTitle">This record</h2>
            </div>
            {isOwner && (
              <Link to="/audit" className="btn btn-ghost btn-sm">
                Full audit
              </Link>
            )}
          </header>
          <div className="bgSectionBody">
            {!isOwner ? (
              <p className="bgSubtle">
                The audit trail for a record is visible to its owner only.
              </p>
            ) : auditEntries.length === 0 ? (
              <p className="bgSubtle">No events recorded for this record yet.</p>
            ) : (
              <ul className="bgAuditSlice">
                {auditEntries
                  .slice()
                  .reverse()
                  .slice(0, 8)
                  .map((e) => (
                    <li key={e.id} className="bgAuditSliceRow">
                      <span className="bgAuditKind">{e.kind}</span>
                      <span className="bgSubtle">{e.actorName}</span>
                      <span className="bgAuditTime">{ago(e.createdAt)}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {showBreak && !convene.share && (
        <BreakGlassDialog
          target={{
            id: record.id,
            description: record.description,
            thresholdK: record.thresholdK,
            totalN: record.totalN,
            holders,
          }}
          timeoutSeconds={record.timeoutSeconds}
          busy={convene.busy}
          error={convene.error}
          onConfirm={(reason) =>
            convene.convene(
              {
                id: record.id,
                description: record.description,
                thresholdK: record.thresholdK,
                totalN: record.totalN,
                holders,
              },
              reason,
            )
          }
          onCancel={() => setShowBreak(false)}
        />
      )}

      {convene.share && (
        <ShareCeremonyPanel
          share={convene.share}
          onEnter={convene.enter}
          onClose={() => {
            convene.dismissShare();
            setShowBreak(false);
          }}
        />
      )}

      {showTransfer && (
        <TransferOwnershipDialog
          recordName={name}
          candidates={candidates}
          busy={transferBusy}
          error={transferError}
          onConfirm={doTransfer}
          onCancel={() => setShowTransfer(false)}
        />
      )}

      {transferError && !showTransfer && <FeedbackMessage ok={false} msg={transferError} />}
    </div>
  );
}

export default RecordDetailPage;
